<template>
  <aside class="benefits-preview" :class="{ 'benefits-preview--fullscreen': fullscreen }">
    <h2 v-if="!fullscreen" class="benefits-preview-title">{{ t("benefitsCenter-preview-title") }}</h2>

    <div class="benefits-preview-controls">
      <BenefitsSegmentedControl
        v-model="activeScreen"
        :options="screenOptions"
        :aria-label="t('benefitsCenter-preview-title')"
        class="benefits-preview-tabs"
      />

      <div v-if="showViewAs" class="benefits-preview-view-as">
        <span class="benefits-preview-view-as-label">{{ t("benefitsCenter-preview-viewAs") }}</span>
        <KvSelect
          v-model="previewTierIds"
          :options="tierOptions"
          multiple
          variant="outlined"
          full-width
          :placeholder="t('benefitsCenter-preview-viewAsPlaceholder')"
        >
          <template #chip="{ option }">
            <BenefitsTierChip :option="option" />
          </template>
        </KvSelect>
      </div>
    </div>

    <div class="benefits-preview-device">
      <div class="benefits-preview-frame">
        <iframe
          v-if="previewUrl"
          ref="frame"
          :src="previewUrl"
          class="benefits-preview-iframe"
          title="preview"
          @load="onFrameLoad"
        ></iframe>

        <div v-else class="benefits-preview-placeholder">
          {{ t("benefitsCenter-preview-placeholder") }}
        </div>
      </div>
    </div>

    <button
      v-if="accessUrl"
      type="button"
      class="benefits-preview-link"
      @click="copyAccessLink"
    >
      <Icon :icon-class="copied ? 'ui-check' : 'ui-copy-03'" size="4" color="#1f2a37" />
      {{ copied ? t("benefitsCenter-preview-linkCopied") : t("benefitsCenter-preview-link") }}
    </button>
  </aside>
</template>

<script>
import BenefitsSegmentedControl from "./benefits-segmented-control.vue";
import KvSelect from "../../components/inputs/kv-select.vue";
import { tierSelectOptions } from "../sections/tiers/tiers-utils";
import BenefitsTierChip from "../sections/tiers/benefits-tier-chip.vue";
import { buildRewardsPlaceUrl, getRewardsOrigin } from "./rewards-url";

export default {
  name: "BenefitsPreview",
  components: { BenefitsSegmentedControl, KvSelect, BenefitsTierChip },
  props: {
    program: {
      type: Object,
      default: () => ({}),
    },
    placeSlug: {
      type: String,
      default: "",
    },
    fullscreen: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeScreen: "app",
      previewTierIds: [],
      ready: false,
      copied: false,
      copyTimer: null,
      sendTimer: null,
    };
  },
  computed: {
    previewUrl() {
      return buildRewardsPlaceUrl(this.placeSlug, { preview: true });
    },
    accessUrl() {
      return buildRewardsPlaceUrl(this.placeSlug);
    },
    rewardsOrigin() {
      return getRewardsOrigin(this.previewUrl);
    },
    previewTiers() {
      const points = this.program.points || {};
      const tiers = this.program.tiers || {};

      return points.enabled ? tiers.items || [] : [];
    },
    tierOptions() {
      return tierSelectOptions(this.previewTiers);
    },
    showViewAs() {
      return this.activeScreen === "app" && this.previewTiers.length > 0;
    },
    screenOptions() {
      return [
        { value: "login", label: this.t("benefitsCenter-preview-screenLogin") },
        { value: "app", label: this.t("benefitsCenter-preview-screenApp") },
      ];
    },
  },
  watch: {
    program: {
      deep: true,
      handler() {
        this.scheduleSend();
      },
    },
    activeScreen() {
      this.sendState();
    },
    previewTierIds() {
      this.sendState();
    },
  },
  mounted() {
    window.addEventListener("message", this.onMessage);
  },
  beforeDestroy() {
    window.removeEventListener("message", this.onMessage);
    clearTimeout(this.copyTimer);
    clearTimeout(this.sendTimer);
  },
  methods: {
    t(term, replaces) {
      return this.$root.t(term, replaces);
    },
    onMessage(event) {
      if (event.origin !== this.rewardsOrigin) {
        return;
      }

      if (event.data && event.data.type === "kevi-preview-ready") {
        this.ready = true;
        this.sendState();
      }
    },
    onFrameLoad() {
      if (this.ready) {
        this.sendState();
      }
    },
    scheduleSend() {
      clearTimeout(this.sendTimer);
      this.sendTimer = setTimeout(this.sendState, 300);
    },
    sendState() {
      const frame = this.$refs.frame;

      if (!frame || !frame.contentWindow || !this.rewardsOrigin) {
        return;
      }

      frame.contentWindow.postMessage(
        {
          type: "kevi-preview",
          program: this.program,
          screen: this.activeScreen,
          previewTierIds: this.previewTierIds.filter((tierId) => this.tierOptions.some((option) => option.value === tierId)),
        },
        this.rewardsOrigin
      );
    },
    copyAccessLink() {
      const done = () => {
        this.copied = true;
        clearTimeout(this.copyTimer);
        this.copyTimer = setTimeout(() => {
          this.copied = false;
        }, 2000);
      };

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(this.accessUrl).then(done, done);
        return;
      }

      done();
    },
  },
};
</script>

<style lang="scss" scoped>
.benefits-preview {
  box-sizing: border-box;
  display: grid;
  gap: 12px;
  grid-template-rows: auto auto minmax(0, 1fr) auto;
  max-height: calc(100vh - 48px);
  min-height: 0;
  overflow: hidden;
  width: 100%;
}

.benefits-preview-title {
  color: #111927;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 600;
  margin: 0;
}

.benefits-preview-controls {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.benefits-preview-tabs {
  justify-self: center;
  max-width: 375px;
  width: 100%;
}

.benefits-preview-view-as {
  align-items: center;
  display: flex;
  gap: 8px;
  max-width: 375px;
  width: 100%;
}

.benefits-preview-view-as-label {
  color: #384250;
  flex: 0 0 auto;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 500;
}

.benefits-preview-frame {
  align-items: center;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  box-sizing: border-box;
  display: flex;
  height: 812px;
  justify-content: center;
  max-width: 100%;
  overflow: hidden;
  width: 375px;
}

.benefits-preview:not(.benefits-preview--fullscreen) .benefits-preview-frame {
  aspect-ratio: 375 / 812;
  height: 100%;
  max-height: 100%;
  max-width: min(375px, 100%);
  width: auto;
}

.benefits-preview-device {
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 0;
  width: 100%;
}

.benefits-preview--fullscreen {
  box-sizing: border-box;
  display: grid;
  gap: 12px;
  grid-template-rows: auto minmax(0, 1fr) auto;
  height: 100%;
  max-height: calc(100vh - 39px);
  min-height: 0;
  overflow: hidden;
  width: 100%;

  .benefits-preview-tabs {
    justify-self: center;
    width: min(375px, 100%);
  }

  .benefits-preview-device {
    align-items: center;
    display: flex;
    justify-content: center;
    min-height: 0;
    width: 100%;
  }

  .benefits-preview-frame {
    aspect-ratio: 375 / 812;
    box-sizing: border-box;
    height: 100%;
    max-height: min(812px, calc(100dvh - 200px));
    max-width: min(375px, 100%);
    width: auto;
  }

  .benefits-preview-link {
    justify-self: stretch;
    margin-right: 64px;
    max-width: none;
    min-height: 44px;
    width: auto;
  }
}

.benefits-preview-iframe {
  border: 0;
  display: block;
  height: 100%;
  width: 100%;
}

.benefits-preview-placeholder {
  color: #6c737f;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  padding: 24px;
  text-align: center;
}

.benefits-preview-link {
  align-items: center;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  color: #1f2a37;
  cursor: pointer;
  display: flex;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  gap: 8px;
  justify-content: center;
  justify-self: center;
  max-width: 375px;
  padding: 12px;
  width: 100%;
}

@media (max-width: 1100px) {
  .benefits-preview:not(.benefits-preview--fullscreen) {
    max-height: none;
    overflow-y: visible;
    position: static;
    width: 100%;
  }
}
</style>
